import { IBulgeArc, bulgeArc } from './arc-bulge';
import {
    EDimOrientation,
    ELineCap,
    ELineJoin,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    IArcPrimitive,
    IDimPrimitive,
    IFont,
    IHatchPrimitive,
    IPaneRect,
    IPlanPalette,
    IPlanPrimitive,
    IPoint,
    IPolyPrimitive,
    ITextPrimitive,
} from './plan-primitive';

const DIM_LINE_WIDTH: number = 0.6;
const DIM_LABEL_GAP: number = 3;
const VERTICAL_LABEL_OFFSET: number = 12;
/** Width of the ground-coloured outline behind a label: lines under a figure break around it. */
const LABEL_HALO_WIDTH: number = 3;

/**
 * Writes a label over a ground-coloured outline of its glyphs, so that a line passing under it
 * breaks around the figure — as a drawing interrupts a line for a dimension figure.
 */
function fillLabel(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, ground: string): void {
    ctx.save();
    ctx.strokeStyle = ground;
    ctx.lineWidth = LABEL_HALO_WIDTH;
    ctx.lineJoin = 'round';
    ctx.strokeText(text, x, y);
    ctx.restore();
    ctx.fillText(text, x, y);
}

function fontToCss(font: IFont): string {
    return `${String(font.weight)} ${String(font.size)}px ${font.family}`;
}

function alignToCanvas(align: ETextAlign): CanvasTextAlign {
    switch (align) {
        case ETextAlign.Left:
            return 'left';
        case ETextAlign.Right:
            return 'right';
        case ETextAlign.Center:
            return 'center';
    }
}

function baselineToCanvas(baseline: ETextBaseline): CanvasTextBaseline {
    switch (baseline) {
        case ETextBaseline.Top:
            return 'top';
        case ETextBaseline.Middle:
            return 'middle';
        case ETextBaseline.Bottom:
            return 'bottom';
    }
}

function capToCanvas(cap: ELineCap): CanvasLineCap {
    return cap === ELineCap.Round ? 'round' : 'butt';
}

function joinToCanvas(join: ELineJoin): CanvasLineJoin {
    return join === ELineJoin.Round ? 'round' : 'miter';
}

function withAlpha(ctx: CanvasRenderingContext2D, alpha: number | undefined, paint: () => void): void {
    if (alpha === undefined || alpha === 1) {
        paint();
        return;
    }
    const previous: number = ctx.globalAlpha;
    ctx.globalAlpha = alpha;
    paint();
    ctx.globalAlpha = previous;
}

/** Segment `index` → `index + 1` of the path: a straight line, or an arc when it carries a bulge. */
function traceSegment(ctx: CanvasRenderingContext2D, from: IPoint, to: IPoint, bulge: number): void {
    if (bulge === 0) {
        ctx.lineTo(to[0], to[1]);
        return;
    }
    const arc: IBulgeArc = bulgeArc(from, to, bulge);
    ctx.arc(arc.center[0], arc.center[1], arc.radius, arc.startAngle, arc.endAngle, arc.anticlockwise);
}

function tracePolyPath(ctx: CanvasRenderingContext2D, prim: IPolyPrimitive): void {
    const points: readonly IPoint[] = prim.points;
    const bulgeAt: (index: number) => number = (index: number): number => prim.bulges?.[index] ?? 0;
    ctx.moveTo(points[0][0], points[0][1]);
    for (let i: number = 1; i < points.length; i++) {
        traceSegment(ctx, points[i - 1], points[i], bulgeAt(i - 1));
    }
    if (prim.closed) {
        traceSegment(ctx, points[points.length - 1], points[0], bulgeAt(points.length - 1));
        ctx.closePath();
    }
}

function drawPoly(ctx: CanvasRenderingContext2D, prim: IPolyPrimitive, palette: IPlanPalette): void {
    if (prim.points.length === 0) {
        return;
    }
    ctx.save();
    ctx.beginPath();
    tracePolyPath(ctx, prim);
    if (prim.fill) {
        ctx.fillStyle = palette[prim.fill];
        withAlpha(ctx, prim.fillAlpha, () => {
            ctx.fill();
        });
    }
    if (prim.stroke) {
        ctx.strokeStyle = palette[prim.stroke];
        ctx.lineWidth = prim.width ?? 1;
        ctx.setLineDash(prim.dash ? [...prim.dash] : []);
        if (prim.cap) {
            ctx.lineCap = capToCanvas(prim.cap);
        }
        if (prim.join) {
            ctx.lineJoin = joinToCanvas(prim.join);
        }
        withAlpha(ctx, prim.strokeAlpha, () => {
            ctx.stroke();
        });
    }
    ctx.restore();
}

function drawArc(ctx: CanvasRenderingContext2D, prim: IArcPrimitive, palette: IPlanPalette): void {
    if (prim.fill) {
        ctx.fillStyle = palette[prim.fill];
        ctx.beginPath();
        ctx.arc(prim.center[0], prim.center[1], prim.radius, prim.startAngle, prim.endAngle, prim.anticlockwise ?? false);
        withAlpha(ctx, prim.fillAlpha, () => {
            ctx.fill();
        });
    }
    if (prim.stroke) {
        ctx.save();
        ctx.strokeStyle = palette[prim.stroke];
        ctx.lineWidth = prim.width ?? 1;
        ctx.setLineDash(prim.dash ? [...prim.dash] : []);
        ctx.beginPath();
        ctx.arc(prim.center[0], prim.center[1], prim.radius, prim.startAngle, prim.endAngle, prim.anticlockwise ?? false);
        withAlpha(ctx, prim.strokeAlpha, () => {
            ctx.stroke();
        });
        ctx.restore();
    }
}

function drawHatch(ctx: CanvasRenderingContext2D, prim: IHatchPrimitive, palette: IPlanPalette): void {
    const { x, y, w, h }: IPaneRect = prim.rect;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.strokeStyle = palette[prim.color];
    ctx.lineWidth = prim.width ?? 1;
    ctx.beginPath();
    for (let d: number = -h; d < w; d += prim.step) {
        ctx.moveTo(x + d, y);
        ctx.lineTo(x + d + h, y + h);
    }
    withAlpha(ctx, prim.alpha, () => {
        ctx.stroke();
    });
    ctx.restore();
}

function drawText(ctx: CanvasRenderingContext2D, prim: ITextPrimitive, palette: IPlanPalette): void {
    ctx.fillStyle = palette[prim.color];
    ctx.font = fontToCss(prim.font);
    ctx.textAlign = alignToCanvas(prim.align);
    ctx.textBaseline = baselineToCanvas(prim.baseline);
    if (prim.rotation) {
        ctx.save();
        ctx.translate(prim.at[0], prim.at[1]);
        ctx.rotate(prim.rotation);
        fillLabel(ctx, prim.text, 0, 0, palette.paper);
        ctx.restore();
        return;
    }
    fillLabel(ctx, prim.text, prim.at[0], prim.at[1], palette.paper);
}

/**
 * A label moved outside its span: the dimension line carries on from the nearer end to the far
 * side of the label, as a drawing does for a dimension too short for its figure.
 */
function drawLeader(ctx: CanvasRenderingContext2D, prim: IDimPrimitive, labelAt: number, middle: number): void {
    const horizontal: boolean = prim.orientation === EDimOrientation.Horizontal;
    const along: (point: readonly [number, number]) => number = (point: readonly [number, number]): number =>
        horizontal ? point[0] : point[1];
    const low: number = Math.min(along(prim.from), along(prim.to));
    const high: number = Math.max(along(prim.from), along(prim.to));
    if (labelAt >= low && labelAt <= high) {
        return;
    }
    const half: number = horizontal ? (prim.labelBox[2] - prim.labelBox[0]) / 2 : (prim.labelBox[3] - prim.labelBox[1]) / 2;
    const start: number = labelAt > middle ? high : low;
    const end: number = labelAt > middle ? labelAt + half : labelAt - half;
    ctx.beginPath();
    if (horizontal) {
        ctx.moveTo(start, prim.from[1]);
        ctx.lineTo(end, prim.from[1]);
    } else {
        ctx.moveTo(prim.from[0], start);
        ctx.lineTo(prim.from[0], end);
    }
    ctx.stroke();
}

function drawDim(ctx: CanvasRenderingContext2D, prim: IDimPrimitive, palette: IPlanPalette): void {
    const color: string = palette[prim.color];
    const [x1, y1]: readonly [number, number] = prim.from;
    const [x2, y2]: readonly [number, number] = prim.to;

    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = DIM_LINE_WIDTH;
    ctx.font = fontToCss(prim.font);

    ctx.beginPath();
    if (prim.orientation === EDimOrientation.Horizontal) {
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.moveTo(x1, y1 - prim.tick);
        ctx.lineTo(x1, y1 + prim.tick);
        ctx.moveTo(x2, y2 - prim.tick);
        ctx.lineTo(x2, y2 + prim.tick);
        ctx.stroke();
        const shift: number = prim.labelShift ?? 0;
        const labelX: number = (x1 + x2) / 2 + shift;
        drawLeader(ctx, prim, labelX, (x1 + x2) / 2);

        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        fillLabel(ctx, prim.label, labelX, y1 - DIM_LABEL_GAP, palette.paper);
    } else {
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.moveTo(x1 - prim.tick, y1);
        ctx.lineTo(x1 + prim.tick, y1);
        ctx.moveTo(x2 - prim.tick, y2);
        ctx.lineTo(x2 + prim.tick, y2);
        ctx.stroke();
        const shift: number = prim.labelShift ?? 0;
        const labelY: number = (y1 + y2) / 2 + shift;
        drawLeader(ctx, prim, labelY, (y1 + y2) / 2);

        ctx.save();
        ctx.translate(x1 + VERTICAL_LABEL_OFFSET, labelY);
        ctx.rotate(-Math.PI / 2);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        fillLabel(ctx, prim.label, 0, 0, palette.paper);
        ctx.restore();
    }
    ctx.restore();
}

/**
 * The Drawer — the only side effect of the plan view. Translates to the pane
 * origin, resolves each colour ref against the (already monochrome-aware)
 * palette, and rasterizes the projection's primitives: the drawing first, then
 * the dimensions and texts over it, so no line runs across a label.
 */
export function drawPlanPrimitives(
    ctx: CanvasRenderingContext2D,
    primitives: readonly IPlanPrimitive[],
    pane: IPaneRect,
    palette: IPlanPalette
): void {
    ctx.save();
    ctx.translate(pane.x, pane.y);
    const labelled: (primitive: IPlanPrimitive) => boolean = (primitive: IPlanPrimitive): boolean =>
        primitive.kind === EPrimitiveKind.Text || primitive.kind === EPrimitiveKind.Dim;
    const ordered: IPlanPrimitive[] = [
        ...primitives.filter((primitive: IPlanPrimitive) => !labelled(primitive)),
        ...primitives.filter(labelled),
    ];
    for (const primitive of ordered) {
        switch (primitive.kind) {
            case EPrimitiveKind.Poly:
                drawPoly(ctx, primitive, palette);
                break;
            case EPrimitiveKind.Text:
                drawText(ctx, primitive, palette);
                break;
            case EPrimitiveKind.Dim:
                drawDim(ctx, primitive, palette);
                break;
            case EPrimitiveKind.Arc:
                drawArc(ctx, primitive, palette);
                break;
            case EPrimitiveKind.Hatch:
                drawHatch(ctx, primitive, palette);
                break;
        }
    }
    ctx.restore();
}
